// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 4 -- "The transition".
//
// A 150-slot annual time series of the seven award systems. Everything stated
// on this page is computed from ctx.core.byYear at render time rather than
// asserted from memory, because the received story ("Imperial honours ended in
// 1992") is not quite what the register says: the last Imperial record in this
// extract is a single award in 2000, and the year the Order of Australia first
// outnumbered Imperial honours is 1983, not 1975.

import type { View } from '../types';
import { systemColour } from '../palette';
import { escapeHtml, longDate, num, pct, tipAttr } from '../format';
import { gloss } from '../glossary';

/** Stack order, bottom to top. Imperial sits directly under the Order of
 *  Australia so the handover reads as one band replacing another. */
const PREFERRED = [
  'Imperial',
  'Order of Australia',
  'Realm',
  'Meritorious',
  'Bravery',
  'Defence',
  'Unit Citation',
];

const SLOT = 8; // user units per calendar year
const LEFT = 58;
const RIGHT = 16;
const PH = 340; // plot height
const BOTTOM = 54;
const ANNO_ROW = 21;
const ANNO_FONT = 12;
const CHAR_W = 6.6; // width estimate per character at ANNO_FONT
const SPARSE_UNTIL = 1900;

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** Axis ticks that land on round numbers without inflating the top of the axis. */
function axisTicks(max: number): { top: number; ticks: number[] } {
  if (!Number.isFinite(max) || max <= 0) return { top: 1, ticks: [0, 1] };
  const rough = max / 6;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const n = rough / mag;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= top + step / 2; t += step) ticks.push(r2(t));
  return { top, ticks };
}

interface Anno {
  year: number;
  x: number;
  text: string;
  tip: string;
  anchor: 'start' | 'end';
  level: number;
}

export const transitionView: View = (root, ctx) => {
  const byYear = [...ctx.core.byYear].sort((a, b) => a.year - b.year);
  if (byYear.length === 0) {
    root.innerHTML = `<div class="empty">The register carries no dated awards, so there is no time series to draw.</div>`;
    return;
  }

  // ---------------------------------------------------------------- the data

  const years = byYear.map((d) => d.year);
  const firstYear = years[0];
  const finalYear = years[years.length - 1];
  const nSlots = Math.max(1, finalYear - firstYear + 1);
  const countsFor = new Map<number, Record<string, number>>(byYear.map((d) => [d.year, d.counts]));

  const present = new Set<string>();
  for (const d of byYear) {
    for (const [k, v] of Object.entries(d.counts)) if (v > 0) present.add(k);
  }
  const stack = [
    ...PREFERRED.filter((s) => present.has(s)),
    ...[...present].filter((s) => !PREFERRED.includes(s)).sort(),
  ];

  const yearTotal = new Map<number, number>();
  const sysTotal = new Map<string, number>();
  for (const d of byYear) {
    let t = 0;
    for (const s of stack) {
      const v = d.counts[s] ?? 0;
      t += v;
      sysTotal.set(s, (sysTotal.get(s) ?? 0) + v);
    }
    yearTotal.set(d.year, t);
  }
  const grandTotal = [...yearTotal.values()].reduce((a, b) => a + b, 0);

  // The five facts this view claims, each derived here rather than assumed.
  const oaStart = byYear.find((d) => (d.counts['Order of Australia'] ?? 0) > 0)?.year ?? null;
  const impYears = byYear.filter((d) => (d.counts['Imperial'] ?? 0) > 0);
  const lastImp = impYears.length > 0 ? impYears[impYears.length - 1] : null;
  const crossover =
    byYear.find(
      (d) =>
        (d.counts['Order of Australia'] ?? 0) > 0 &&
        (d.counts['Order of Australia'] ?? 0) > (d.counts['Imperial'] ?? 0),
    )?.year ?? null;
  const parallelYears = oaStart !== null && crossover !== null ? crossover - oaStart : null;

  // "Highest ever" excludes the part year, always.
  let peakYear = firstYear;
  let peakN = -1;
  for (const y of years) {
    if (y === finalYear && years.length > 1) continue;
    const t = yearTotal.get(y) ?? 0;
    if (t > peakN) {
      peakN = t;
      peakYear = y;
    }
  }
  if (peakN < 0) {
    peakYear = finalYear;
    peakN = yearTotal.get(finalYear) ?? 0;
  }

  const sparseYears = years.filter((y) => y < SPARSE_UNTIL);
  const sparseN = sparseYears.reduce((a, y) => a + (yearTotal.get(y) ?? 0), 0);
  const sparseSpan = sparseYears.length > 0 ? SPARSE_UNTIL - firstYear : 0;

  const rawMax = Math.max(1, ...years.map((y) => yearTotal.get(y) ?? 0));
  const { top: absTop, ticks: absTicks } = axisTicks(rawMax);

  const awardN = (name: string): number | null =>
    ctx.core.awards.find((a) => a.name === name)?.n ?? null;
  const sportsMedal = awardN('Australian Sports Medal');
  const centenaryMedal = awardN('Centenary Medal');
  const nationalMedal = awardN('National Medal');

  const xOf = (y: number): number => LEFT + (y - firstYear) * SLOT;
  const xMid = (y: number): number => xOf(y) + SLOT / 2;
  const plotRight = LEFT + nSlots * SLOT;
  const W = plotRight + RIGHT;

  // ------------------------------------------------------- the annotations

  const rawAnnos: { year: number; text: string; tip: string }[] = [];
  if (sparseYears.length > 0) {
    rawAnnos.push({
      year: firstYear,
      text: `before 1900 · ${num(sparseN)} records`,
      tip: `The register is almost empty before 1900: ${num(sparseN)} records across ${num(sparseYears.length)} years in a ${num(sparseSpan)}-year span.\nEarly Imperial awards were not systematically transcribed, so the flat left-hand end of this chart is a gap in the record, not a quiet century.\nClick to pin ${firstYear}.`,
    });
  }
  if (oaStart !== null) {
    rawAnnos.push({
      year: oaStart,
      text: `${oaStart} · Order of Australia created`,
      tip: `${oaStart}: Australia's own honours system begins, with ${num(countsFor.get(oaStart)?.['Order of Australia'] ?? 0)} awards in its first year.\nImperial honours did not stop: ${num(countsFor.get(oaStart)?.['Imperial'] ?? 0)} were still gazetted that year.\nClick to pin ${oaStart}.`,
    });
  }
  if (crossover !== null) {
    const cImp = countsFor.get(crossover)?.['Imperial'] ?? 0;
    const prev = years[years.indexOf(crossover) - 1];
    const pImp = prev === undefined ? null : (countsFor.get(prev)?.['Imperial'] ?? 0);
    rawAnnos.push({
      year: crossover,
      text: `${crossover} · Imperial awards collapse`,
      tip: `${crossover} is the first year the Order of Australia outnumbers Imperial honours.\nImperial awards fall to ${num(cImp)}${pImp !== null && pImp > 0 ? ` from ${num(pImp)} the year before` : ''} — a step change, not a trend.\nClick to pin ${crossover}.`,
    });
  }
  if (lastImp !== null) {
    rawAnnos.push({
      year: lastImp.year,
      text: `${lastImp.year} · last Imperial record`,
      tip: `${lastImp.year} holds the final Imperial record in this extract: ${num(lastImp.counts['Imperial'] ?? 0)}.\nA thin tail runs on past the collapse because some Imperial awards remain in the Sovereign's personal gift rather than being recommended by an Australian government.\nClick to pin ${lastImp.year}.`,
    });
  }
  if (peakN > 0) {
    rawAnnos.push({
      year: peakYear,
      text: `${peakYear} · peak: ${num(peakN)}`,
      tip: `${peakYear} is the highest year on record, ${num(peakN)} awards — driven by one-off commemorative medals, not by a surge in distinction.\nClick to pin ${peakYear}.`,
    });
  }
  if (years.length > 1) {
    rawAnnos.push({
      year: finalYear,
      text: `${finalYear} · part year`,
      tip: `${finalYear} is incomplete. The extract's most recent award is dated ${longDate(ctx.meta.as_at_award_date)}, so this column is hatched and excluded from every "highest ever" statement here.\nClick to pin ${finalYear}.`,
    });
  }

  const annos: Anno[] = [];
  const levelRight: number[] = [];
  for (const a of rawAnnos) {
    const x = xMid(a.year);
    const w = Math.max(24, a.text.length * CHAR_W);
    const anchor: 'start' | 'end' = x + w > plotRight ? 'end' : 'start';
    const left = anchor === 'start' ? x : x - w;
    let level = 0;
    while (level < levelRight.length && left < levelRight[level] + 10) level++;
    levelRight[level] = left + w;
    annos.push({ year: a.year, x, text: a.text, tip: a.tip, anchor, level });
  }
  const nLevels = Math.max(1, levelRight.length);
  const TOP = 14 + nLevels * ANNO_ROW + 10;
  const H = TOP + PH + BOTTOM;

  const uid = `tr${Math.random().toString(36).slice(2, 8)}`;

  // ---------------------------------------------------------------- tooltips

  function breakdown(y: number): { s: string; n: number; share: number }[] {
    const c = countsFor.get(y) ?? {};
    const t = yearTotal.get(y) ?? 0;
    return stack
      .map((s) => ({ s, n: c[s] ?? 0, share: t > 0 ? (c[s] ?? 0) / t : 0 }))
      .filter((r) => r.n > 0)
      .sort((a, b) => b.n - a.n);
  }

  function yearTipText(y: number): string {
    const t = yearTotal.get(y) ?? 0;
    const lines: string[] = [`${y}${y === finalYear && years.length > 1 ? ' — part year' : ''}`];
    lines.push(`${num(t)} award${t === 1 ? '' : 's'} in total`);
    for (const r of breakdown(y)) lines.push(`${r.s}: ${num(r.n)} (${pct(r.share, 1)})`);
    if (y === finalYear && years.length > 1) {
      lines.push(`Incomplete — extract runs to ${longDate(ctx.meta.as_at_award_date)}`);
    }
    if (y < SPARSE_UNTIL) lines.push('Pre-1900 years are sparsely recorded');
    lines.push('Click to pin this year');
    return lines.join('\n');
  }

  // ------------------------------------------------------------------- state

  const params = new URLSearchParams(root.dataset.params ?? '');
  let mode: 'abs' | 'pct' = params.get('mode') === 'pct' ? 'pct' : 'abs';
  const wantYear = Number(params.get('year'));
  let selYear: number | null = years.includes(wantYear) ? wantYear : null;
  let selSys: string | null = null;
  const wantSys = params.get('system');
  if (wantSys !== null && stack.includes(wantSys)) selSys = wantSys;
  let cursor = selYear ?? finalYear;

  // -------------------------------------------------------------- the markup

  const nmShare = nationalMedal !== null && grandTotal > 0 ? nationalMedal / grandTotal : null;

  root.innerHTML = `
<style>
  /* Wide by nature: 150 calendar years. The floor keeps decade labels legible
     (12 user units render at ~9.4px here) and .chart-wrap scrolls locally, so
     the page itself never scrolls sideways at 375px. Capped at W so a
     degenerate one-year series is not stretched to giant text. */
  .${uid}-svg { min-width: ${Math.min(1000, W)}px; }
  .${uid}-svg:focus-visible { outline: 3px solid var(--accent-secondary); outline-offset: 2px; }
  .${uid} .tr-seg { pointer-events: none; }
  .${uid} .tr-hit { fill: transparent; cursor: pointer; }
  .${uid} .tr-hit:hover { fill: rgba(31, 58, 95, 0.09); }
  .${uid} .tr-anno {
    font-size: ${ANNO_FONT}px;
    fill: var(--text-primary);
    cursor: pointer;
    paint-order: stroke;
    stroke: var(--bg-panel);
    stroke-width: 3px;
    stroke-linejoin: round;
  }
  .${uid} .tr-anno:hover { fill: var(--accent-primary-hover); }
  .${uid} .tr-axis { font-size: 12px; }
  .${uid} .tr-axis-title { font-size: 12px; fill: var(--text-tertiary); }
  .${uid}-legend-btn {
    font: inherit;
    font-size: var(--font-size-sm);
    background: none;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    padding: 0.2rem 0.45rem;
    min-height: 40px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--space-xs);
    color: var(--text-secondary);
    min-width: 0;
    max-width: 100%;
  }
  .${uid}-legend-btn:hover { background: var(--bg-hover); }
  .${uid}-legend-btn[aria-pressed='true'] {
    border-color: var(--border-strong);
    background: var(--accent-wash);
    color: var(--text-primary);
    font-weight: 600;
  }
  .${uid}-legend-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .${uid}-legend-n {
    flex-shrink: 0;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    color: var(--text-tertiary);
  }
  .${uid}-key {
    display: inline-flex;
    align-items: center;
    gap: var(--space-xs);
    color: var(--text-tertiary);
    min-width: 0;
  }
  .${uid}-pick {
    display: inline-flex;
    align-items: center;
    gap: var(--space-sm);
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    min-width: 0;
  }
  .${uid}-pick select { max-width: 9rem; min-width: 0; }
  .${uid}-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .${uid}-swatch-hatch {
    background-image: repeating-linear-gradient(
      45deg, var(--text-muted) 0 2px, transparent 2px 5px
    );
    background-color: var(--bg-elevated);
  }
</style>
<div class="${uid}">
  <div class="view-head">
    <h1>The transition</h1>
    <p class="lede">
      For most of the twentieth century Australians were honoured by Britain. In ${oaStart ?? 1975}
      Australia created ${gloss('order', 'its own honours system')}, and
      ${gloss('imperial', 'Imperial honours')} were wound back. This chart draws every dated award in
      the register — ${num(grandTotal)} of them across ${num(years.length)} years — so you can see
      whether that was a gradual drift or a clean break. It was a break, and it happened
      ${parallelYears !== null ? `${num(parallelYears)} years after the new system started` : 'later than the founding year'}.
    </p>
  </div>

  <div class="stats">
    <div class="stat" data-tip="${tipAttr(
      `${oaStart ?? '—'} is the first year in the register carrying an Order of Australia award: ${num(
        oaStart === null ? 0 : (countsFor.get(oaStart)?.['Order of Australia'] ?? 0),
      )} of them.`,
    )}">
      <div class="stat-value">${oaStart ?? '—'}</div>
      <div class="stat-label">First Order of Australia awards</div>
    </div>
    <div class="stat" data-tip="${tipAttr(
      crossover === null
        ? 'No year in the register has more Order of Australia awards than Imperial awards.'
        : `${crossover}: Order of Australia ${num(
            countsFor.get(crossover)?.['Order of Australia'] ?? 0,
          )} against Imperial ${num(countsFor.get(crossover)?.['Imperial'] ?? 0)}.`,
    )}">
      <div class="stat-value">${crossover ?? '—'}</div>
      <div class="stat-label">First year it outnumbered Imperial honours</div>
    </div>
    <div class="stat" data-tip="${tipAttr(
      `The two systems ran side by side for ${
        parallelYears === null ? '—' : num(parallelYears)
      } years. Imperial honours were not switched off the day the Order of Australia was created.`,
    )}">
      <div class="stat-value">${parallelYears === null ? '—' : num(parallelYears)}</div>
      <div class="stat-label">Years the two systems ran in parallel</div>
    </div>
    <div class="stat" data-tip="${tipAttr(
      lastImp === null
        ? 'No Imperial awards in the register.'
        : `${lastImp.year} carries the last Imperial record in this extract: ${num(
            lastImp.counts['Imperial'] ?? 0,
          )}. The commonly repeated end date is 1992; this extract runs a little past it.`,
    )}">
      <div class="stat-value">${lastImp === null ? '—' : lastImp.year}</div>
      <div class="stat-label">Last Imperial record in the register</div>
    </div>
  </div>

  <section class="panel">
    <div class="panel-head">
      <h2>Awards gazetted each year, by award system, ${firstYear}–${finalYear}</h2>
      <p class="panel-sub">
        A stacked column for every calendar year. Each column is one year; the coloured bands inside it
        are the seven award systems, stacked, so the height of the whole column is that year's total and
        the height of each band is that system's share of it. Imperial sits at the bottom and the Order
        of Australia directly above it, so the handover reads as one band replacing another.
        Switch to <strong>composition</strong> to see shares instead of counts — a share can rise while
        the count falls, so the two modes answer different questions.
        Hover any year for its exact breakdown; click to pin it.
      </p>
    </div>

    <div class="controls">
      <div class="control-group" role="group" aria-label="Chart mode">
        <button type="button" data-mode="abs" aria-pressed="${mode === 'abs'}">Absolute counts</button>
        <button type="button" data-mode="pct" aria-pressed="${mode === 'pct'}">Composition (100%)</button>
      </div>
      <label class="${uid}-pick">Pin a year
        <select data-role="yearpick" aria-label="Pin a year to see its breakdown">
          <option value="">— none —</option>
          ${years
            .map(
              (y) =>
                `<option value="${y}"${y === selYear ? ' selected' : ''}>${y} · ${num(
                  yearTotal.get(y) ?? 0,
                )}</option>`,
            )
            .join('')}
        </select>
      </label>
    </div>

    <div class="chart-wrap" data-role="wrap"></div>

    <div class="legend" data-role="legend"></div>

    <p class="${uid}-sr" aria-live="polite" data-role="live"></p>

    <div class="note">
      <strong>Read the height as volume, not merit.</strong>
      The tallest band on this chart is ${gloss('realm', 'Realm')}, and it is dominated by the
      ${gloss('national-medal', 'National Medal')}${
        nmShare === null ? '' : ` — ${num(nationalMedal ?? 0)} records, ${pct(nmShare, 1)} of the register`
      }, a long-service medal for fifteen years of diligent service. A tall column means many people
      were ${gloss('gazette', 'gazetted')} that year, nothing more.
      ${
        sportsMedal !== null && centenaryMedal !== null
          ? `The two towers at the turn of the century are one-off commemorative issues — the Australian
             Sports Medal (${num(sportsMedal)} awards) and the Centenary Medal (${num(centenaryMedal)}) —
             and they say more about the year 2001 than about anyone's service.`
          : ''
      }
      Early years are extremely sparse: ${
        sparseYears.length > 0
          ? `just ${num(sparseN)} records exist before 1900, across ${num(sparseYears.length)} scattered years`
          : 'the register thins out badly at its left-hand end'
      }, so the flat start is a gap in transcription, not a quiet century.
      The final column, ${finalYear}, is <strong>incomplete</strong> — the most recent award in this
      extract is dated ${longDate(ctx.meta.as_at_award_date)} — so it is drawn hatched and is excluded
      from the peak figure above.
    </div>
  </section>

  <section class="panel" data-role="detail"></section>
</div>`;

  const wrap = root.querySelector<HTMLDivElement>('[data-role="wrap"]')!;
  const legendEl = root.querySelector<HTMLDivElement>('[data-role="legend"]')!;
  const detailEl = root.querySelector<HTMLElement>('[data-role="detail"]')!;
  const liveEl = root.querySelector<HTMLElement>('[data-role="live"]')!;
  const pickEl = root.querySelector<HTMLSelectElement>('[data-role="yearpick"]')!;

  // ---------------------------------------------------------------- the SVG

  function buildSvg(): string {
    const isPct = mode === 'pct';
    const ticks = isPct ? [0, 0.25, 0.5, 0.75, 1] : absTicks;
    const tickTop = isPct ? 1 : absTop;
    const yFor = (v: number): number =>
      TOP + PH - (tickTop > 0 ? Math.max(0, Math.min(1, v / tickTop)) * PH : 0);

    const parts: string[] = [];

    parts.push(`<defs>
      <pattern id="${uid}-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="none"/>
        <line x1="0" y1="0" x2="0" y2="6" stroke="#1b2330" stroke-width="2" opacity="0.45"/>
      </pattern>
    </defs>`);

    // Sparse-era wash, drawn under everything.
    if (sparseYears.length > 0) {
      const x0 = xOf(firstYear);
      const x1 = Math.min(plotRight, xOf(SPARSE_UNTIL));
      if (x1 > x0) {
        parts.push(
          `<rect x="${r2(x0)}" y="${TOP}" width="${r2(x1 - x0)}" height="${PH}" fill="#8a6a1f" opacity="0.06" pointer-events="none"/>`,
        );
      }
    }

    // Y gridlines + labels.
    for (const t of ticks) {
      const y = r2(yFor(t));
      parts.push(
        `<line x1="${LEFT}" y1="${y}" x2="${plotRight}" y2="${y}" stroke="#cfc9ba" stroke-width="1" opacity="${t === 0 ? 1 : 0.6}" pointer-events="none"/>`,
        `<text class="tr-axis" x="${LEFT - 8}" y="${y + 4}" text-anchor="end" pointer-events="none">${
          isPct ? `${Math.round(t * 100)}%` : num(t)
        }</text>`,
      );
    }

    // X ticks: decades.
    const firstDecade = Math.ceil(firstYear / 10) * 10;
    for (let y = firstDecade; y <= finalYear; y += 10) {
      const x = r2(xMid(y));
      parts.push(
        `<line x1="${x}" y1="${TOP + PH}" x2="${x}" y2="${TOP + PH + 5}" stroke="#a49b86" stroke-width="1" pointer-events="none"/>`,
        `<text class="tr-axis" x="${x}" y="${TOP + PH + 20}" text-anchor="middle" pointer-events="none">${y}</text>`,
      );
    }

    // Axis titles.
    parts.push(
      `<text class="tr-axis-title" x="${r2((LEFT + plotRight) / 2)}" y="${TOP + PH + 42}" text-anchor="middle" pointer-events="none">Year the award was gazetted</text>`,
      `<text class="tr-axis-title" transform="translate(14 ${r2(TOP + PH / 2)}) rotate(-90)" text-anchor="middle" pointer-events="none">${
        isPct ? 'Share of that year’s awards' : 'Awards gazetted'
      }</text>`,
    );

    // Columns.
    const bar = SLOT - 1.6;
    for (const y of years) {
      const c = countsFor.get(y) ?? {};
      const total = yearTotal.get(y) ?? 0;
      if (isPct && total <= 0) continue;
      const denom = isPct ? total : 1;
      const x = r2(xOf(y) + 0.8);
      let acc = 0;
      for (const s of stack) {
        const v = c[s] ?? 0;
        if (v <= 0) continue;
        const yTop = yFor(isPct ? (acc + v) / denom : acc + v);
        const yBot = yFor(isPct ? acc / denom : acc);
        acc += v;
        const h = Math.max(0, yBot - yTop);
        parts.push(
          `<rect class="tr-seg" data-year="${y}" data-sys="${escapeHtml(s)}" x="${x}" y="${r2(yTop)}" width="${r2(bar)}" height="${r2(h)}" fill="${systemColour(s)}"/>`,
        );
      }
      // The part year is hatched and outlined so it can never be read as a full year.
      if (y === finalYear && years.length > 1 && total > 0) {
        const yTop = yFor(isPct ? 1 : total);
        parts.push(
          `<rect class="tr-seg" data-year="${y}" data-sys="__partial" x="${x}" y="${r2(yTop)}" width="${r2(bar)}" height="${r2(TOP + PH - yTop)}" fill="url(#${uid}-hatch)"/>`,
          `<rect class="tr-seg" data-year="${y}" data-sys="__partial" x="${x}" y="${r2(yTop)}" width="${r2(bar)}" height="${r2(TOP + PH - yTop)}" fill="none" stroke="#1b2330" stroke-width="1" stroke-dasharray="3 2"/>`,
        );
      }
    }

    // Annotations: leader lines first, haloed labels second.
    for (const a of annos) {
      const labelY = 14 + (nLevels - 1 - a.level) * ANNO_ROW + ANNO_FONT;
      const x = r2(a.x);
      parts.push(
        `<line x1="${x}" y1="${r2(labelY + 4)}" x2="${x}" y2="${TOP + 6}" stroke="#7c8698" stroke-width="1" stroke-dasharray="2 2" pointer-events="none"/>`,
        `<circle cx="${x}" cy="${TOP + 6}" r="2.4" fill="#4d5768" pointer-events="none"/>`,
      );
    }
    for (const a of annos) {
      const labelY = 14 + (nLevels - 1 - a.level) * ANNO_ROW + ANNO_FONT;
      parts.push(
        `<text class="tr-anno" data-year="${a.year}" data-tip="${tipAttr(a.tip)}" role="button" tabindex="-1" aria-label="${escapeHtml(a.text)}. Click to pin ${a.year}." x="${r2(a.x)}" y="${r2(labelY)}" text-anchor="${a.anchor}">${escapeHtml(a.text)}</text>`,
      );
    }

    // Selection + keyboard cursor outlines, positioned later.
    parts.push(
      `<rect data-role="cursor" x="0" y="${TOP}" width="${SLOT}" height="${PH}" fill="none" stroke="#1f3a5f" stroke-width="1.4" stroke-dasharray="4 3" visibility="hidden" pointer-events="none"/>`,
      `<rect data-role="selbox" x="0" y="${TOP}" width="${SLOT}" height="${PH}" fill="none" stroke="#1b2330" stroke-width="1.6" visibility="hidden" pointer-events="none"/>`,
    );

    // Hit targets: one full-height rect per year, so hovering anywhere in the
    // column's slot yields that year's whole breakdown.
    for (const y of years) {
      parts.push(
        `<rect class="tr-hit" data-year="${y}" data-tip="${tipAttr(yearTipText(y))}" role="button" aria-label="${escapeHtml(
          yearTipText(y).replace(/\n/g, '. '),
        )}" x="${r2(xOf(y))}" y="${TOP}" width="${SLOT}" height="${PH}"/>`,
      );
    }

    return `<svg class="chart ${uid}-svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"
      preserveAspectRatio="xMinYMin meet" role="group" tabindex="0"
      aria-label="Stacked column chart: Australian honours gazetted each year from ${firstYear} to ${finalYear}, by award system. Use the left and right arrow keys to move between years and Enter to pin one.">${parts.join(
      '',
    )}</svg>`;
  }

  function buildLegend(): void {
    const items = stack
      .map((s) => {
        const n = sysTotal.get(s) ?? 0;
        const share = grandTotal > 0 ? n / grandTotal : 0;
        const yrs = years.filter((y) => (countsFor.get(y)?.[s] ?? 0) > 0);
        const tip = `${s}\n${num(n)} awards, ${pct(share, 1)} of the register\nAppears in ${num(
          yrs.length,
        )} years${yrs.length > 0 ? `, ${yrs[0]}–${yrs[yrs.length - 1]}` : ''}\nClick to trace this system and dim the rest`;
        return `<button type="button" class="${uid}-legend-btn" data-sys="${escapeHtml(s)}"
          aria-pressed="${selSys === s}" data-tip="${tipAttr(tip)}"
          aria-label="${escapeHtml(s)}, ${num(n)} awards. Trace this system.">
          <span class="swatch" style="background:${systemColour(s)}"></span>
          <span class="${uid}-legend-name">${escapeHtml(s)}</span>
          <span class="${uid}-legend-n">${num(n)}</span>
        </button>`;
      })
      .join('');
    const key =
      years.length > 1
        ? `<span class="${uid}-key" data-tip="${tipAttr(
            `${finalYear} is a part year — the extract's most recent award is dated ${longDate(
              ctx.meta.as_at_award_date,
            )}. Hatching marks it as incomplete.`,
          )}"><span class="swatch ${uid}-swatch-hatch"></span>${finalYear}: part year, incomplete</span>`
        : '';
    legendEl.innerHTML = items + key;
  }

  // ------------------------------------------------------------- the detail

  function renderDetail(): void {
    if (selYear !== null) {
      const rows = breakdown(selYear);
      const total = yearTotal.get(selYear) ?? 0;
      const partial = selYear === finalYear && years.length > 1;
      detailEl.innerHTML = `
        <div class="panel-head">
          <h2>${selYear}: ${num(total)} award${total === 1 ? '' : 's'}${partial ? ' (part year)' : ''}</h2>
          <p class="panel-sub">
            Every award system recorded in ${selYear}, largest first. Click a row to trace that system
            across the whole chart.
            ${
              partial
                ? `This year is incomplete — the extract's most recent award is dated ${longDate(
                    ctx.meta.as_at_award_date,
                  )}, so do not compare it with a full year.`
                : ''
            }
            ${
              selYear < SPARSE_UNTIL
                ? 'Years before 1900 are very sparsely transcribed; a low count here is a gap in the record.'
                : ''
            }
          </p>
        </div>
        <div class="scroller">
          <table>
            <thead><tr><th scope="col">Award system</th><th scope="col" class="n">Awards</th><th scope="col" class="n">Share of ${selYear}</th></tr></thead>
            <tbody>
              ${rows
                .map(
                  (r) => `<tr data-row data-sys="${escapeHtml(r.s)}" data-tip="${tipAttr(
                    `${r.s} in ${selYear}: ${num(r.n)} awards, ${pct(r.share, 1)} of that year's ${num(
                      total,
                    )}.\nAcross the whole register: ${num(sysTotal.get(r.s) ?? 0)}.\nClick to trace this system.`,
                  )}">
                  <td><span class="swatch" style="background:${systemColour(
                    r.s,
                  )};display:inline-block;vertical-align:-2px;margin-right:.4rem"></span>${escapeHtml(r.s)}</td>
                  <td class="n">${num(r.n)}</td>
                  <td class="n">${pct(r.share, 1)}</td>
                </tr>`,
                )
                .join('')}
            </tbody>
          </table>
        </div>
        <div class="controls" style="margin-top:var(--space-md);margin-bottom:0">
          <button type="button" class="pill" data-role="clear-year">Unpin ${selYear}</button>
        </div>`;
      return;
    }

    if (selSys !== null) {
      const s = selSys;
      const yrs = years.filter((y) => (countsFor.get(y)?.[s] ?? 0) > 0);
      const n = sysTotal.get(s) ?? 0;
      let best = yrs[0] ?? firstYear;
      let bestN = -1;
      for (const y of yrs) {
        const v = countsFor.get(y)?.[s] ?? 0;
        if (v > bestN) {
          bestN = v;
          best = y;
        }
      }
      detailEl.innerHTML = `
        <div class="panel-head">
          <h2><span class="swatch" style="background:${systemColour(
            s,
          )};display:inline-block;vertical-align:-2px;margin-right:.4rem"></span>${escapeHtml(s)}</h2>
          <p class="panel-sub">The chart is dimmed to this system alone. Pin a year to see how it sat
            alongside the others that year.</p>
        </div>
        <div class="stats" style="margin-bottom:0">
          <div class="stat" data-tip="${tipAttr(
            `${s}: ${num(n)} awards, ${pct(grandTotal > 0 ? n / grandTotal : 0, 1)} of the ${num(
              grandTotal,
            )} dated records.`,
          )}">
            <div class="stat-value">${num(n)}</div><div class="stat-label">Awards in total</div>
          </div>
          <div class="stat" data-tip="${tipAttr(
            `${s} appears in ${num(yrs.length)} separate years of the register.`,
          )}">
            <div class="stat-value">${yrs.length > 0 ? `${yrs[0]}–${yrs[yrs.length - 1]}` : '—'}</div>
            <div class="stat-label">First and last year on the register</div>
          </div>
          <div class="stat" data-tip="${tipAttr(
            `${best} is the busiest year for ${s}: ${num(Math.max(0, bestN))} awards.`,
          )}">
            <div class="stat-value">${yrs.length > 0 ? best : '—'}</div>
            <div class="stat-label">Busiest year (${num(Math.max(0, bestN))} awards)</div>
          </div>
        </div>
        <div class="controls" style="margin-top:var(--space-lg);margin-bottom:0">
          <button type="button" class="pill" data-role="clear-sys">Stop tracing ${escapeHtml(s)}</button>
        </div>`;
      return;
    }

    detailEl.innerHTML = `
      <div class="panel-head">
        <h2>Nothing pinned</h2>
        <p class="panel-sub">
          Hover any year for its exact breakdown, or click a year — on the chart, on an annotation, or
          from the “Pin a year” list — to hold it here. Click an award system in the legend to trace it
          across all ${num(years.length)} years and dim the rest.
        </p>
      </div>
      <p class="panel-sub">
        The one thing worth checking for yourself: switch to composition and watch
        ${gloss('imperial', 'Imperial')} hand over to ${gloss('order', 'the Order of Australia')}
        between ${oaStart ?? '—'} and ${crossover ?? '—'}. In absolute counts the same years look
        almost flat, which is exactly why both modes are here.
      </p>`;
  }

  // ------------------------------------------------------------------ paint

  let svgEl: SVGSVGElement | null = null;

  function paint(): void {
    if (!svgEl) return;
    svgEl.querySelectorAll<SVGRectElement>('.tr-seg').forEach((r) => {
      const y = Number(r.dataset.year);
      const s = r.dataset.sys ?? '';
      let o = 1;
      if (selSys !== null && s !== selSys && s !== '__partial') o *= 0.13;
      if (selYear !== null && y !== selYear) o *= 0.28;
      r.setAttribute('opacity', String(r2(o)));
    });

    const sel = svgEl.querySelector<SVGRectElement>('[data-role="selbox"]');
    if (sel) {
      if (selYear === null) sel.setAttribute('visibility', 'hidden');
      else {
        sel.setAttribute('x', String(r2(xOf(selYear))));
        sel.setAttribute('visibility', 'visible');
      }
    }
    const cur = svgEl.querySelector<SVGRectElement>('[data-role="cursor"]');
    if (cur) {
      const focused = document.activeElement === svgEl;
      if (!focused) cur.setAttribute('visibility', 'hidden');
      else {
        cur.setAttribute('x', String(r2(xOf(cursor))));
        cur.setAttribute('visibility', 'visible');
      }
    }

    legendEl.querySelectorAll<HTMLButtonElement>(`.${uid}-legend-btn`).forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.sys === selSys));
    });
    root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.mode === mode));
    });
    if (pickEl.value !== (selYear === null ? '' : String(selYear))) {
      pickEl.value = selYear === null ? '' : String(selYear);
    }
  }

  function drawChart(): void {
    wrap.innerHTML = buildSvg();
    svgEl = wrap.querySelector<SVGSVGElement>('svg');
    paint();
  }

  function setYear(y: number | null): void {
    selYear = y !== null && years.includes(y) ? y : null;
    if (selYear !== null) cursor = selYear;
    renderDetail();
    paint();
  }

  function setSys(s: string | null): void {
    selSys = s !== null && stack.includes(s) ? s : null;
    renderDetail();
    paint();
  }

  // --------------------------------------------------------------- wiring

  wrap.addEventListener('click', (e) => {
    const hit = (e.target as Element).closest<Element>('[data-year]');
    if (!hit) return;
    const y = Number(hit.getAttribute('data-year'));
    if (!Number.isFinite(y)) return;
    setYear(selYear === y ? null : y);
  });

  wrap.addEventListener('keydown', (e) => {
    if (!svgEl || document.activeElement !== svgEl) return;
    const i = years.indexOf(cursor);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = Math.max(0, Math.min(years.length - 1, (i < 0 ? 0 : i) + (e.key === 'ArrowRight' ? 1 : -1)));
      cursor = years[next];
      liveEl.textContent = yearTipText(cursor).replace(/\n/g, '. ');
      paint();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      cursor = e.key === 'Home' ? years[0] : years[years.length - 1];
      liveEl.textContent = yearTipText(cursor).replace(/\n/g, '. ');
      paint();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setYear(selYear === cursor ? null : cursor);
      liveEl.textContent =
        selYear === null ? 'Year unpinned.' : `${selYear} pinned. ${yearTipText(selYear).replace(/\n/g, '. ')}`;
    } else if (e.key === 'Escape') {
      setYear(null);
      setSys(null);
      liveEl.textContent = 'Selection cleared.';
    }
  });

  wrap.addEventListener('focusin', paint);
  wrap.addEventListener('focusout', paint);

  legendEl.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>(`.${uid}-legend-btn`);
    if (!btn) return;
    const s = btn.dataset.sys ?? '';
    setSys(selSys === s ? null : s);
  });

  detailEl.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest('[data-role="clear-year"]')) {
      setYear(null);
      return;
    }
    if (target.closest('[data-role="clear-sys"]')) {
      setSys(null);
      return;
    }
    const row = target.closest<HTMLElement>('tr[data-row]');
    if (row) {
      const s = row.dataset.sys ?? '';
      setSys(selSys === s ? null : s);
    }
  });

  root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) => {
    b.addEventListener('click', () => {
      const next = b.dataset.mode === 'pct' ? 'pct' : 'abs';
      if (next === mode) return;
      mode = next;
      drawChart();
    });
  });

  pickEl.addEventListener('change', () => {
    const v = pickEl.value;
    setYear(v === '' ? null : Number(v));
    if (selYear !== null && svgEl) {
      // Bring the pinned year into view inside the locally-scrolling chart.
      const scale = svgEl.clientWidth > 0 ? svgEl.clientWidth / W : 1;
      wrap.scrollLeft = Math.max(0, xOf(selYear) * scale - wrap.clientWidth / 2);
    }
  });

  buildLegend();
  drawChart();
  renderDetail();
};
